import { type Theme, type ThemeColor } from '@precis-digital/kurama/theme'
import { type MultipleSelectionChoiceProps } from '../components/MultipleSelection'
import type { SingleSelectionChoiceProps } from '../components/SingleSelection'
import {
  chartMetricShowLegendBadge,
  CHART_CARD_EXPORT_LAYOUT,
  isLineChartType,
  linePatterns,
  SelectedOthers
} from '../constants'
import type {
  ChartCardDefaults,
  BaseElementDescription,
  ChartCardTFunction,
  ChartChoiceOptions,
  ChartDataRow,
  ChartSelections,
  ChartType,
  LinePattern,
  SelectionValue
} from '../types'
import type {
  AxisType,
  ChartAxisChoice,
  ChartChoice,
  ChartDimensionChoice,
  ChartDimensionValueChoice,
  ChartDimensionValueOfChoice,
  ChartDimensionValuesChoice,
  ChartDimensionValuesOfChoice,
  ChartMetricChoice,
  ChartMetricsChoice,
  ChartMetricTransposedChoice,
  ChartMetricVariantsChoice,
  ChartVariantValueChoice,
  ChartVariantValuesChoice,
  ExportLegendItem,
  MultipleSelectExportLegendItem,
  SelectedExportLegendItem
} from '../internalTypes'
import {
  assertNever,
  errorLocal,
  generateIdForSelectionOthers,
  getLabelOrValueAsLabel,
  getValueFromTransposedMetricColumn,
  singleToPlural
} from './generics'
import { createSVGTextItem, setCanvasContextFont } from './graph'

const generateSingleSelectionExportLegendItem = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  props: SingleSelectionChoiceProps<DataKey, DataRow> & {
    showBadge?: boolean
    showPattern?: boolean
    hideLabel?: boolean
    singleLegendItems: Array<ExportLegendItem<DataKey>>
  }
): void => {
  const { choice, selected, showBadge, showPattern, hideLabel, singleLegendItems } = props
  const { id, type, label, color: choiceColor } = choice

  let options: ChartChoiceOptions<DataKey> | ChartChoiceOptions<string> | undefined

  if (type === 'valueOf') {
    const { otherSelected } = props
    options = choice.change.options[otherSelected as DataKey]
  } else {
    options = choice.change.options
  }

  const {
    label: selectedLabel,
    color: selectedColor,
    pattern: selectedPattern,
    alert: selectedAlert,
    alertText: selectedAlertText
  } = options.find(({ value }) => value === selected) ?? {}

  const legendItem: ExportLegendItem<DataKey> = {
    id,
    label: showBadge === true || hideLabel === true ? undefined : label ?? id,
    value: selectedLabel ?? selected,
    alert: selectedAlert ?? (selectedAlertText == null ? undefined : 'warning')
  }

  if (showBadge === true) {
    const badgeColor = selectedColor ?? choiceColor
    if (badgeColor != null) {
      legendItem.color = badgeColor
      if (showPattern === true) {
        legendItem.pattern = selectedPattern
      }
    }
  }

  singleLegendItems.push(legendItem)
}

const generateVariantValueExportLegendItem = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  selections,
  singleLegendItems
}: {
  choice: ChartMetricChoice<DataKey, DataRow> | ChartMetricTransposedChoice<DataKey, DataRow>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  selections: ChartSelections<DataKey>
  singleLegendItems: Array<ExportLegendItem<DataKey>>
}): boolean => {
  const { id: choiceId, type, variant } = choice

  if (variant == null) {
    return false
  }

  const variantValueChoice = choicesMap[choice.variant] as
    | ChartVariantValueChoice<DataKey, DataRow>
    | ChartVariantValuesChoice<DataKey, DataRow>

  if (variantValueChoice.type === 'variantValues') {
    return false
  }

  const selectedVariantValue = selections[variantValueChoice.id]
  if (typeof selectedVariantValue !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selectedVariantValue`, {
      choice,
      variantValueChoice,
      selectedVariantValue
    })
    return true
  }

  generateSingleSelectionExportLegendItem<DataKey, DataRow>({
    type: variantValueChoice.type,
    choice: variantValueChoice,
    selected: selectedVariantValue,
    singleLegendItems
  })

  return false
}

const generateChoiceMetricExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  selections,
  selected,
  showBadge,
  showPattern,
  singleLegendItems
}: {
  choice: ChartMetricChoice<DataKey, DataRow>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  selections: ChartSelections<DataKey>
  selected: SelectionValue<DataKey> | undefined
  showBadge: boolean
  showPattern: boolean
  singleLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { axis, id: choiceId, type } = choice

  if (axis === 'first' || !showBadge) {
    return
  }

  if (typeof selected !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  if (generateVariantValueExportLegendItem<DataKey, DataRow>({ choice, choicesMap, selections, singleLegendItems })) {
    return
  }

  generateSingleSelectionExportLegendItem<DataKey, DataRow>({
    type,
    choice,
    selected,
    showBadge,
    showPattern,
    singleLegendItems
  })
}

const createChipExportLegendItem = <DataKey extends string>({
  chartType,
  pattern,
  ...otherProps
}: {
  chartType: ChartType
  value: string
} & Omit<BaseElementDescription, 'alertText'>): SelectedExportLegendItem<DataKey> =>
  isLineChartType(chartType)
    ? {
        pattern,
        ...otherProps
      }
    : otherProps

const generateChipsExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  t,
  showBadge,
  showPattern,
  chipsLegendItems,
  ...props
}: MultipleSelectionChoiceProps<DataKey, DataRow> & {
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { type, choice, parentChoice, sortedValues, selected, selectedOthers } = props
  const { id, chartType, color: choiceColor, pattern: choicePattern } = choice
  const { color: parentChoiceColor, pattern: parentChoicePattern } = parentChoice ?? {}
  const { label = id } = choice

  const options = type === 'valuesOf' ? choice.change.options[props.otherSelected as DataKey] : choice.change.options

  const chipsItems: Array<SelectedExportLegendItem<DataKey>> = []
  const chipsLegendItem: MultipleSelectExportLegendItem<DataKey> = {
    id,
    label: singleToPlural(label, options.length),
    items: chipsItems
  }
  chipsLegendItems.push(chipsLegendItem)

  for (const selectedValue of sortedValues ?? selected) {
    if (!selected.includes(selectedValue)) {
      continue
    }
    const option = options.find(({ value }) => value === selectedValue)
    if (option == null) {
      errorLocal(`generateChoiceMetricsLegendItems ${id} ${type} invalid option`, { options, selectedValue })
      continue
    }
    const { value, color, label, pattern, alert, alertText } = option

    chipsItems.push(
      createChipExportLegendItem({
        chartType,
        value: getLabelOrValueAsLabel(label, value),
        color: color ?? choiceColor ?? parentChoiceColor,
        pattern: pattern ?? choicePattern ?? parentChoicePattern,
        alert: alert ?? (alertText == null ? undefined : 'warning')
      })
    )
  }

  if (selectedOthers === true) {
    const otherOption = options.find(({ value }) => !selected.includes(value))
    if (otherOption != null) {
      const { color, pattern, alert, alertText } = otherOption
      chipsItems.push(
        createChipExportLegendItem({
          chartType,
          value: t('others'),
          color: color ?? choiceColor ?? parentChoiceColor,
          pattern: pattern ?? choicePattern ?? parentChoicePattern,
          alert: alert ?? (alertText == null ? undefined : 'warning')
        })
      )
    }
  }
}

const generateChoiceMetricsExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selected,
  t,
  showBadge,
  showPattern,
  chipsLegendItems
}: {
  choice: ChartMetricsChoice<DataKey, DataRow>
  selected: SelectionValue<DataKey> | undefined
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { axis, id: choiceId, type } = choice

  if (axis === 'first' || !showBadge) {
    return
  }

  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  generateChipsExportLegendItems<DataKey, DataRow>({
    type,
    choice,
    selected,
    t,
    showBadge,
    showPattern,
    chipsLegendItems
  })
}

const generateChoiceMetricTransposedValuesChoiceExportLegendItems = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  choice,
  dimensionValuesChoice,
  selectedDimension,
  metrics,
  selections,
  t,
  showBadge,
  showPattern,
  chipsLegendItems
}: {
  choice: ChartChoice<DataKey, DataRow>
  dimensionValuesChoice: ChartDimensionValuesChoice<DataKey, DataRow> | ChartDimensionValuesOfChoice<DataKey, DataRow>
  selectedDimension: DataKey | undefined
  metrics: Set<DataKey> | undefined
  selections: ChartSelections<DataKey>
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { axis, id: valuesChoiceId } = dimensionValuesChoice

  if (axis === 'first' || !showBadge) {
    return
  }

  let selectedDimensionValues = selections[valuesChoiceId]
  const selectedOthers = selections[generateIdForSelectionOthers(valuesChoiceId)] === SelectedOthers

  if (!Array.isArray(selectedDimensionValues)) {
    errorLocal(`generateChoiceLegendItems ${choice.id} ${choice.type} selectedDimensionValues invalid`, {
      choice,
      dimensionValuesChoice,
      selectedDimensionValues
    })
    return
  }

  const sortedValues: string[] | undefined =
    metrics == null ? undefined : Array.from(metrics).map((metric) => getValueFromTransposedMetricColumn(metric))
  if (sortedValues != null) {
    selectedDimensionValues = sortedValues.filter((value) => (selectedDimensionValues as string[]).includes(value))
  }

  if (dimensionValuesChoice.type === 'values') {
    generateChipsExportLegendItems<DataKey, DataRow>({
      type: dimensionValuesChoice.type,
      choice: dimensionValuesChoice,
      parentChoice: choice,
      sortedValues,
      selected: selectedDimensionValues,
      selectedOthers,
      t,
      showBadge,
      showPattern,
      chipsLegendItems
    })
  } else {
    generateChipsExportLegendItems<DataKey, DataRow>({
      type: dimensionValuesChoice.type,
      choice: dimensionValuesChoice,
      parentChoice: choice,
      sortedValues,
      selected: selectedDimensionValues,
      selectedOthers,
      otherSelected: selectedDimension as string,
      t,
      showBadge,
      showPattern,
      chipsLegendItems
    })
  }
}

const generateChoiceMetricTransposedExportLegendItems = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  choice,
  choicesMap,
  transposedMetricsLists,
  selections,
  selected,
  t,
  showBadge,
  showPattern,
  singleLegendItems,
  chipsLegendItems
}: {
  choice: ChartMetricTransposedChoice<DataKey, DataRow>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
  selected: SelectionValue<DataKey> | undefined
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { id: choiceId, type } = choice

  if (typeof selected !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  if (showPattern || (!showPattern && !showBadge)) {
    generateSingleSelectionExportLegendItem<DataKey, DataRow>({
      type,
      choice,
      selected,
      hideLabel: true,
      singleLegendItems
    })
  }

  if (generateVariantValueExportLegendItem<DataKey, DataRow>({ choice, choicesMap, selections, singleLegendItems })) {
    return
  }

  const dimensionValuesChoice = choicesMap[choice.transpose] as
    | ChartDimensionValuesChoice<DataKey, DataRow>
    | ChartDimensionValuesOfChoice<DataKey, DataRow>
    | undefined

  if (dimensionValuesChoice == null) {
    return
  }

  const dimensionChoice = (
    dimensionValuesChoice.dimension === choice.transpose ? undefined : choicesMap[dimensionValuesChoice.dimension]
  ) as ChartDimensionChoice<DataKey, DataRow> | undefined

  if (dimensionValuesChoice.type === 'values') {
    if (dimensionChoice != null) {
      errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid dimensionValuesChoice`, {
        choice,
        dimensionValuesChoice,
        dimensionChoice
      })
      return
    }
  }
  if (dimensionChoice != null && dimensionChoice.type !== 'dimension') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid dimensionChoice`, { choice, dimensionChoice })
    return
  }
  let selectedDimension: DataKey | undefined
  if (dimensionChoice != null) {
    selectedDimension = selections[dimensionChoice.id] as DataKey
    if (typeof selectedDimension !== 'string') {
      errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selectedDimension`, {
        choice,
        dimensionChoice,
        selectedDimension
      })
      return
    }
    generateSingleSelectionExportLegendItem<DataKey, DataRow>({
      type: dimensionChoice.type,
      choice: dimensionChoice,
      selected: selectedDimension,
      singleLegendItems
    })
  }

  generateChoiceMetricTransposedValuesChoiceExportLegendItems({
    choice,
    dimensionValuesChoice,
    selectedDimension,
    metrics: transposedMetricsLists.get(choiceId),
    selections,
    t,
    showBadge,
    showPattern,
    chipsLegendItems
  })
}

const generateDimensionChoiceExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  dimensionChoice,
  selections,
  singleLegendItems
}: {
  choice: ChartChoice<DataKey, DataRow>
  dimensionChoice: ChartDimensionChoice<DataKey, DataRow>
  selections: ChartSelections<DataKey>
  singleLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const selectedDimension = selections[dimensionChoice.id] as DataKey
  if (typeof selectedDimension !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choice.id} ${choice.type} invalid selectedDimension`, {
      choice,
      dimensionChoice,
      selectedDimension
    })
    return
  }
  generateSingleSelectionExportLegendItem<DataKey, DataRow>({
    type: dimensionChoice.type,
    choice: dimensionChoice,
    selected: selectedDimension,
    singleLegendItems
  })
}

const geberateDimensionValueChoiceExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  dimensionValueChoice,
  selections,
  showBadge,
  showPattern,
  singleLegendItems
}: {
  choice: ChartChoice<DataKey, DataRow>
  dimensionValueChoice: ChartDimensionValueChoice<DataKey, DataRow> | ChartDimensionValueOfChoice<DataKey, DataRow>
  selections: ChartSelections<DataKey>
  showBadge: boolean
  showPattern: boolean
  singleLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { id: valueChoiceId, dimension } = dimensionValueChoice
  const selectedDimensionValue = selections[valueChoiceId] as string

  if (typeof selectedDimensionValue !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choice.id} ${choice.type} invalid selectedDimensionValue`, {
      choice,
      dimensionValueChoice,
      selectedDimensionValue
    })
    return
  }

  if (dimensionValueChoice.type === 'value') {
    generateSingleSelectionExportLegendItem<DataKey, DataRow>({
      type: dimensionValueChoice.type,
      choice: dimensionValueChoice,
      selected: selectedDimensionValue,
      showBadge,
      showPattern,
      singleLegendItems
    })
  } else {
    const selectedDimension = selections[dimension] as DataKey
    if (typeof selectedDimension !== 'string') {
      errorLocal(`generateChoiceLegendItems ${choice.id} ${choice.type} invalid selectedDimension`, {
        choice,
        dimensionValueChoice,
        selectedDimension
      })
      return
    }
    generateSingleSelectionExportLegendItem<DataKey, DataRow>({
      type: dimensionValueChoice.type,
      choice: dimensionValueChoice,
      selected: selectedDimensionValue,
      otherSelected: selectedDimension as string,
      showBadge,
      showPattern,
      singleLegendItems
    })
  }
}

const generateChoiceMetricVariantsTransposedExportLegendItems = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  choice,
  choicesMap,
  selections,
  showBadge,
  showPattern,
  singleLegendItems
}: {
  choice: ChartMetricVariantsChoice<DataKey, DataRow>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  selections: ChartSelections<DataKey>
  showBadge: boolean
  showPattern: boolean
  singleLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { id: choiceId, type, transpose } = choice

  const dimensionValueChoice = choicesMap[transpose] as
    | ChartDimensionValueChoice<DataKey, DataRow>
    | ChartDimensionValueOfChoice<DataKey, DataRow>
    | undefined

  if (dimensionValueChoice == null) {
    return
  }

  const dimensionChoice = (
    dimensionValueChoice.dimension === transpose ? undefined : choicesMap[dimensionValueChoice.dimension]
  ) as ChartDimensionChoice<DataKey, DataRow> | undefined

  if (dimensionValueChoice.type === 'value') {
    if (dimensionChoice != null) {
      errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid dimensionValueChoice`, {
        choice,
        dimensionValueChoice,
        dimensionChoice
      })
      return
    }
  }
  if (dimensionChoice != null && dimensionChoice.type !== 'dimension') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid dimensionChoice`, {
      choice,
      dimensionChoice
    })
    return
  }
  if (dimensionChoice != null) {
    generateDimensionChoiceExportLegendItems({
      choice,
      dimensionChoice,
      selections,
      singleLegendItems
    })
  }
  if (dimensionValueChoice != null) {
    geberateDimensionValueChoiceExportLegendItems({
      choice,
      dimensionValueChoice,
      selections,
      showBadge,
      showPattern,
      singleLegendItems
    })
  }
}

const generateChoiceMetricVariantsExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  selections,
  selected,
  t,
  showBadge,
  showPattern,
  singleLegendItems,
  chipsLegendItems
}: {
  choice: ChartMetricVariantsChoice<DataKey, DataRow>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  selections: ChartSelections<DataKey>
  selected: SelectionValue<DataKey> | undefined
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { axis, id: choiceId, type } = choice

  if (typeof selected !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  const variantValuesChoice = choice.variant == null ? undefined : choicesMap[choice.variant]

  if (variantValuesChoice?.type !== 'variantValues') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid variantValuesChoice`, {
      choice,
      variantValuesChoice
    })
    return
  }

  const variantValues = selections[variantValuesChoice.id]
  if (!Array.isArray(variantValues)) {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid variantValues`, {
      choice,
      variantValuesChoice,
      variantValues
    })
    return
  }

  if ((axis === 'first' || !showBadge) && choice.transpose == null) {
    return
  }

  generateChipsExportLegendItems<DataKey, DataRow>({
    type: variantValuesChoice.type,
    choice: variantValuesChoice,
    parentChoice: choice,
    selected: variantValues,
    t,
    showBadge,
    showPattern,
    chipsLegendItems
  })

  if (choice.transpose != null && axis !== 'first') {
    generateChoiceMetricVariantsTransposedExportLegendItems({
      choice,
      choicesMap,
      selections,
      showBadge: false,
      showPattern,
      singleLegendItems
    })
  }

  if (showPattern || (!showPattern && !showBadge)) {
    generateSingleSelectionExportLegendItem<DataKey, DataRow>({
      type,
      choice,
      selected,
      hideLabel: true,
      singleLegendItems
    })
  }
}

const generateChoiceValuesExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selected,
  t,
  showBadge,
  showPattern,
  chipsLegendItems
}: {
  choice: ChartDimensionValuesChoice<DataKey, DataRow>
  selected: SelectionValue<DataKey> | undefined
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { id: choiceId, type } = choice

  if (!showBadge) {
    return
  }

  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  generateChipsExportLegendItems<DataKey, DataRow>({
    type,
    choice,
    selected,
    selectedOthers: false,
    t,
    showBadge,
    showPattern,
    chipsLegendItems
  })
}

const generateChoiceValuesOfExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  selections,
  selected,
  t,
  showBadge,
  showPattern,
  singleLegendItems,
  chipsLegendItems
}: {
  choice: ChartDimensionValuesOfChoice<DataKey, DataRow>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  selections: ChartSelections<DataKey>
  selected: SelectionValue<DataKey> | undefined
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { id: choiceId, type, dimension } = choice

  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  const dimensionChoice = dimension == null ? undefined : choicesMap[dimension]
  if (dimensionChoice?.type !== 'dimension') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid dimensionChoice`, { choice, dimensionChoice })
    return
  }

  const selectedDimension = selections[dimensionChoice.id]
  if (typeof selectedDimension !== 'string') {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selectedDimension`, {
      choice,
      dimensionChoice,
      selectedDimension
    })
    return
  }

  generateSingleSelectionExportLegendItem<DataKey, DataRow>({
    type: dimensionChoice.type,
    choice: dimensionChoice,
    selected: selectedDimension,
    singleLegendItems
  })

  if (!showBadge) {
    return
  }

  generateChipsExportLegendItems<DataKey, DataRow>({
    type,
    choice,
    selected,
    selectedOthers: selections[generateIdForSelectionOthers(choiceId)] === SelectedOthers,
    otherSelected: selectedDimension,
    t,
    showBadge,
    showPattern,
    chipsLegendItems
  })
}

const generateChoiceVariantValuesExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selected,
  t,
  showBadge,
  showPattern,
  chipsLegendItems
}: {
  choice: ChartVariantValuesChoice<DataKey, DataRow>
  selected: SelectionValue<DataKey> | undefined
  t: ChartCardTFunction
  showBadge: boolean
  showPattern: boolean
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
}): void => {
  const { id: choiceId, type } = choice

  if (!showBadge) {
    return
  }

  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceLegendItems ${choiceId} ${type} invalid selected`, { choice, selected })
    return
  }

  generateChipsExportLegendItems<DataKey, DataRow>({
    type,
    choice,
    selected,
    t,
    showBadge,
    showPattern,
    chipsLegendItems
  })
}

const getSingleAndChipsExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  singleLegendItems,
  chipsLegendItems,
  togetherLegendItems,
  choicesTogether
}: {
  choice: ChartAxisChoice<DataKey, DataRow>
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
  togetherLegendItems: Map<AxisType, Array<ExportLegendItem<DataKey>>>
  choicesTogether: boolean
}): { singleLegendItems: Array<ExportLegendItem<DataKey>>; chipsLegendItems: Array<ExportLegendItem<DataKey>> } => {
  if (choicesTogether) {
    const { axis } = choice
    let legendItems = togetherLegendItems.get(axis)

    if (legendItems == null) {
      legendItems = []
      togetherLegendItems.set(axis, legendItems)
    }

    return { singleLegendItems: legendItems, chipsLegendItems: legendItems }
  }

  return { singleLegendItems, chipsLegendItems }
}

const generateChoiceExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  singleLegendItems: singleLegendItemsProp,
  chipsLegendItems: chipsLegendItemsProp,
  togetherLegendItems,
  choicesTogether,
  choiceId,
  choicesMap,
  transposedMetricsLists,
  selections,
  t
}: {
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
  togetherLegendItems: Map<AxisType, Array<ExportLegendItem<DataKey>>>
  choicesTogether: boolean
  choiceId: DataKey
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
  t: ChartCardTFunction
}): void => {
  const choice = choicesMap[choiceId] as ChartAxisChoice<DataKey, DataRow>

  if (choice == null) {
    errorLocal(`generateChoiceLegendItems ${choiceId} missing choice`, { choicesMap })
    return
  }

  const { axis, id, type, chartType } = choice
  const showBadge =
    ((axis === 'first' || axis === 'third') && !chartMetricShowLegendBadge[chartType]) ||
    ((axis === 'second' || axis === 'otherSecond') && chartMetricShowLegendBadge[chartType])
  const showPattern = (axis === 'second' || axis === 'otherSecond') && isLineChartType(chartType)

  const selected = selections[id]

  const { singleLegendItems, chipsLegendItems } = getSingleAndChipsExportLegendItems({
    choice,
    singleLegendItems: singleLegendItemsProp,
    chipsLegendItems: chipsLegendItemsProp,
    togetherLegendItems,
    choicesTogether
  })

  switch (type) {
    case 'metric':
      generateChoiceMetricExportLegendItems<DataKey, DataRow>({
        choice,
        choicesMap,
        selections,
        selected,
        showBadge,
        showPattern,
        singleLegendItems
      })
      break
    case 'metrics':
      generateChoiceMetricsExportLegendItems<DataKey, DataRow>({
        choice,
        selected,
        t,
        showBadge,
        showPattern,
        chipsLegendItems
      })
      break
    case 'metricTransposed':
      generateChoiceMetricTransposedExportLegendItems<DataKey, DataRow>({
        choice,
        choicesMap,
        transposedMetricsLists,
        selections,
        selected,
        t,
        showBadge,
        showPattern,
        singleLegendItems,
        chipsLegendItems
      })
      break
    case 'metricVariants':
      generateChoiceMetricVariantsExportLegendItems<DataKey, DataRow>({
        choice,
        choicesMap,
        selections,
        selected,
        t,
        showBadge,
        showPattern,
        singleLegendItems,
        chipsLegendItems
      })
      break
    case 'values':
      generateChoiceValuesExportLegendItems<DataKey, DataRow>({
        choice,
        selected,
        t,
        showBadge,
        showPattern,
        chipsLegendItems
      })
      break
    case 'valuesOf':
      generateChoiceValuesOfExportLegendItems<DataKey, DataRow>({
        choice,
        choicesMap,
        selections,
        selected,
        t,
        showBadge,
        showPattern,
        singleLegendItems,
        chipsLegendItems
      })
      break
    case 'variantValues':
      generateChoiceVariantValuesExportLegendItems<DataKey, DataRow>({
        choice,
        selected,
        t,
        showBadge,
        showPattern,
        chipsLegendItems
      })
      break
    default:
      assertNever(type)
  }
}

export const generateChoicesExportLegendItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choicesList,
  choicesMap,
  choicesTogether,
  transposedMetricsLists,
  selections,
  t
}: {
  choicesList: DataKey[]
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  choicesTogether: boolean
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
  t: ChartCardTFunction
}): {
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
  togetherLegendItems: Map<AxisType, Array<ExportLegendItem<DataKey>>>
} => {
  const singleLegendItems: Array<ExportLegendItem<DataKey>> = []
  const chipsLegendItems: Array<ExportLegendItem<DataKey>> = []
  const togetherLegendItems = new Map<AxisType, Array<ExportLegendItem<DataKey>>>()

  for (const choiceId of choicesList) {
    generateChoiceExportLegendItems<DataKey, DataRow>({
      singleLegendItems,
      chipsLegendItems,
      togetherLegendItems,
      choicesTogether,
      choiceId,
      choicesMap,
      transposedMetricsLists,
      selections,
      t
    })
  }

  return { singleLegendItems, chipsLegendItems, togetherLegendItems }
}

const getLabelWidth = (
  label: string | undefined,
  context: CanvasRenderingContext2D,
  defaults: ChartCardDefaults
): number => {
  if (label == null) {
    return 0
  }
  return setCanvasContextFont(context, defaults.fonts.exportLegendLabels).measureText(`${label}:`).width
}

const drawExportLegendColorIndicator = ({
  pattern,
  color,
  offsetX,
  legendSVGItem,
  lineHeight,
  theme
}: {
  pattern: LinePattern | undefined
  color: ThemeColor
  offsetX: number
  legendSVGItem: SVGGElement
  lineHeight: number
  theme: Theme
}): number => {
  if (pattern == null) {
    const radius = theme.borderRadius.xxSmall
    const indicator = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    indicator.setAttribute('width', '12')
    indicator.setAttribute('height', '12')
    indicator.setAttribute('x', offsetX.toString())
    indicator.setAttribute('rx', radius)
    indicator.setAttribute('ry', radius)
    indicator.setAttribute('fill', color)
    legendSVGItem.appendChild(indicator)
    offsetX += 12
  } else {
    const verticalMiddle = (lineHeight * 0.5).toString()
    const linePattern = linePatterns[pattern]
    const indicator = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    indicator.setAttribute('transform', `translate(${offsetX}, ${verticalMiddle})`)
    indicator.setAttribute('width', '30')
    indicator.setAttribute('height', '4')
    indicator.setAttribute('d', 'M2 2H28')
    indicator.setAttribute('stroke', color)
    indicator.setAttribute('stroke-width', '4')
    indicator.setAttribute('stroke-linecap', 'round')
    if (linePattern != null) indicator.setAttribute('stroke-dasharray', linePattern)
    legendSVGItem.appendChild(indicator)
    offsetX += 30
  }

  return offsetX + 6
}

const createSingleExportLegendSVGItem = <DataKey extends string>({
  legendGroup,
  legendItem,
  firstOrAlone,
  labelAlert,
  lineHeight,
  padding,
  verticalSpacing,
  xStart,
  labelGap,
  xGap,
  xPosition,
  yPosition,
  firstLabelWidth,
  width,
  context,
  defaults,
  theme
}: {
  legendGroup: SVGGElement
  legendItem: SelectedExportLegendItem<DataKey>
  firstOrAlone: boolean
  labelAlert: SelectedExportLegendItem<DataKey>['alert']
  lineHeight: number
  padding: number
  verticalSpacing: number
  xStart: number
  labelGap: number
  xGap: number
  xPosition: number
  yPosition: number
  firstLabelWidth: number
  width: number
  context: CanvasRenderingContext2D
  defaults: ChartCardDefaults
  theme: Theme
}): { xPosition: number; yPosition: number; xPositionAfterLabel: number } => {
  const { label, value, color, pattern, alert } = legendItem

  const legendSVGItem = document.createElementNS('http://www.w3.org/2000/svg', 'g')

  let offsetX = 0

  if (label != null && firstOrAlone) {
    offsetX +=
      createSVGTextItem<DataKey>({
        text: label,
        asLabel: true,
        xOffset: offsetX,
        minWidth: xPosition === xStart ? firstLabelWidth : 0,
        context,
        container: legendSVGItem,
        fontProps: defaults.fonts.exportLegendLabels,
        alert: labelAlert
      }).width + labelGap
  }

  const offsetXAfterLabel = offsetX
  const backgroundRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  backgroundRect.setAttribute('fill', '#f5f5f5')
  backgroundRect.setAttribute('rx', theme.borderRadius.small.toString())
  backgroundRect.setAttribute('ry', theme.borderRadius.small.toString())
  backgroundRect.setAttribute('x', offsetXAfterLabel.toString())
  backgroundRect.setAttribute('y', (-padding * 0.9).toString())
  offsetX += padding

  legendSVGItem.appendChild(backgroundRect)

  if (color != null) {
    offsetX = drawExportLegendColorIndicator({
      pattern,
      color,
      offsetX,
      legendSVGItem,
      lineHeight,
      theme
    })
  }

  offsetX += createSVGTextItem<DataKey>({
    text: value,
    asLabel: false,
    xOffset: offsetX,
    context,
    container: legendSVGItem,
    fontProps: defaults.fonts.exportLegendChips,
    alert
  }).width

  backgroundRect.setAttribute('width', (offsetX + padding - offsetXAfterLabel).toString())
  backgroundRect.setAttribute('height', (lineHeight + padding * 2).toString())

  let xPositionAfterLabel = 0
  if (xPosition + offsetX + padding > width) {
    yPosition += lineHeight + verticalSpacing + padding * 2
    legendSVGItem.setAttribute('transform', `translate(${xStart}, ${yPosition + padding})`)
    xPosition = xStart + offsetX + padding + xGap
    xPositionAfterLabel = xStart + offsetXAfterLabel
  } else {
    if (xPosition !== xStart && firstOrAlone) {
      xPosition += padding
    }
    legendSVGItem.setAttribute('transform', `translate(${xPosition}, ${yPosition + padding})`)
    xPositionAfterLabel = xPosition + offsetXAfterLabel
    xPosition += offsetX + padding + xGap
  }

  legendGroup.appendChild(legendSVGItem)

  return {
    xPosition,
    yPosition,
    xPositionAfterLabel
  }
}

const createExportLegendSVGItems = <DataKey extends string>({
  legendGroup,
  legendItemOrItems,
  lineHeight,
  padding,
  verticalSpacing,
  xStart,
  xPosition,
  yPosition,
  firstLabelWidth,
  width,
  context,
  defaults,
  theme
}: {
  legendGroup: SVGGElement
  legendItemOrItems: ExportLegendItem<DataKey>
  lineHeight: number
  padding: number
  verticalSpacing: number
  xStart: number
  xPosition: number
  yPosition: number
  firstLabelWidth: number
  width: number
  context: CanvasRenderingContext2D
  defaults: ChartCardDefaults
  theme: Theme
}): { xPosition: number; yPosition: number; newLineYPosition: number } => {
  const { items, label, alert } = legendItemOrItems
  if (items == null) {
    const result = createSingleExportLegendSVGItem<DataKey>({
      legendGroup,
      legendItem: legendItemOrItems,
      firstOrAlone: true,
      labelAlert: undefined,
      lineHeight,
      padding,
      xStart,
      verticalSpacing,
      xGap: CHART_CARD_EXPORT_LAYOUT.itemsGap,
      labelGap: CHART_CARD_EXPORT_LAYOUT.labelGap,
      xPosition,
      yPosition,
      firstLabelWidth,
      width,
      context,
      defaults,
      theme
    })
    xPosition = result.xPosition
    yPosition = result.yPosition
  } else {
    let groupXStart = xPosition
    for (const [index, legendItem] of items.entries()) {
      const result = createSingleExportLegendSVGItem<DataKey>({
        legendGroup,
        legendItem: { ...legendItem, label },
        firstOrAlone: index === 0,
        labelAlert: alert,
        lineHeight,
        padding,
        verticalSpacing,
        xStart: groupXStart,
        labelGap: CHART_CARD_EXPORT_LAYOUT.labelGap,
        xGap: CHART_CARD_EXPORT_LAYOUT.groupedItemsGap,
        xPosition,
        yPosition,
        firstLabelWidth: xPosition === xStart ? firstLabelWidth : 0,
        width,
        context,
        defaults,
        theme
      })
      if (index === 0 && label != null) {
        groupXStart = result.xPositionAfterLabel
      }
      xPosition = result.xPosition
      yPosition = result.yPosition
    }
  }

  return {
    xPosition,
    yPosition,
    newLineYPosition: xPosition === xStart ? yPosition : yPosition + lineHeight + verticalSpacing + padding * 2
  }
}

export const createExportLegendsSVGItems = <DataKey extends string>({
  legendGroup,
  singleLegendItems,
  chipsLegendItems,
  togetherLegendItems,
  xStart,
  yStart,
  width,
  context,
  defaults,
  theme
}: {
  legendGroup: SVGGElement
  singleLegendItems: Array<ExportLegendItem<DataKey>>
  chipsLegendItems: Array<ExportLegendItem<DataKey>>
  togetherLegendItems: Map<AxisType, Array<ExportLegendItem<DataKey>>>
  xStart: number
  yStart: number
  width: number
  context: CanvasRenderingContext2D
  defaults: ChartCardDefaults
  theme: Theme
}): number => {
  if (singleLegendItems.length === 0 && chipsLegendItems.length === 0 && togetherLegendItems.size === 0) {
    return yStart
  }

  const { fontBoundingBoxAscent: lineHeight } = setCanvasContextFont(
    context,
    defaults.fonts.exportLegendChips
  ).measureText(' ')
  const padding = lineHeight
  const verticalSpacing = lineHeight * 0.5

  const firstLabelWidth = Math.max(
    getLabelWidth(singleLegendItems[0]?.label, context, defaults),
    ...chipsLegendItems.map<number>(({ label }) => getLabelWidth(label, context, defaults)),
    ...Array.from(togetherLegendItems.values()).map<number>((legendItems) =>
      getLabelWidth(legendItems[0]?.label, context, defaults)
    )
  )

  let xPosition = xStart
  let yPosition = yStart
  let newLineYPosition = yStart
  for (const legendItemOrItems of singleLegendItems) {
    const result = createExportLegendSVGItems({
      legendGroup,
      legendItemOrItems,
      lineHeight,
      padding,
      verticalSpacing,
      xStart,
      xPosition,
      yPosition,
      firstLabelWidth,
      width,
      context,
      defaults,
      theme
    })
    xPosition = result.xPosition
    yPosition = result.yPosition
    newLineYPosition = result.newLineYPosition
  }
  for (const legendItemOrItems of chipsLegendItems) {
    const result = createExportLegendSVGItems({
      legendGroup,
      legendItemOrItems,
      lineHeight,
      padding,
      verticalSpacing,
      xStart,
      xPosition: xStart,
      yPosition: newLineYPosition,
      firstLabelWidth,
      width,
      context,
      defaults,
      theme
    })
    newLineYPosition = result.newLineYPosition
  }
  for (const legendItems of togetherLegendItems.values()) {
    for (const [index, legendItemOrItems] of legendItems.entries()) {
      const result = createExportLegendSVGItems({
        legendGroup,
        legendItemOrItems,
        lineHeight,
        padding,
        verticalSpacing,
        xStart,
        xPosition: index === 0 ? xStart : xPosition,
        yPosition: index === 0 ? newLineYPosition : yPosition,
        firstLabelWidth,
        width,
        context,
        defaults,
        theme
      })
      xPosition = result.xPosition
      yPosition = result.yPosition
      newLineYPosition = result.newLineYPosition
    }
  }

  return newLineYPosition
}
